import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    ArrowUpRight,
    ArrowDown,
    BedDouble,
    Building,
    Building2,
    Download,
    Factory,
    FileText,
    Globe,
    Home,
    Mail,
    MapPin,
    Phone,
    Quote,
    Search,
    ShieldCheck,
    Smartphone,
    Store,
    Waypoints,
    Wrench,
} from "lucide-react";
import { KDS } from "../data/kds";
import { useContent } from "../i18n/content";
import { MaskedLine, Reveal } from "../components/motion/Reveal";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";

const ICONS = { BedDouble, Building, Waypoints, Wrench, Factory, Home, Store };

const statusColor = (s) =>
    s === "Completed"
        ? "text-[#f6c422] border-[#f6c422]/40"
        : s === "Proposal"
          ? "text-white/80 border-white/25"
          : "text-[#ffe169] border-[#ffe169]/50";

const pill = (active) =>
    `font-display text-[10px] sm:text-[11px] font-bold tracking-[0.14em] uppercase px-4 py-2 border transition-colors duration-300 ${
        active
            ? "bg-[#f6c422] text-[#292a24] border-[#f6c422]"
            : "border-[#4a4c42] text-[#a2a59d] hover:border-[#f6c422] hover:text-[#f6c422]"
    }`;

const Block = ({ overline, title, children, dark = false, testid, id }) => (
    <section id={id} data-testid={testid} className={`py-20 lg:py-28 ${dark ? "bg-[#24251f]" : ""}`}>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <Reveal>
                <div className="flex items-center gap-4 mb-4">
                    <span className="h-px w-10 bg-[#f6c422]/60" aria-hidden="true" />
                    <span className="overline-text">{overline}</span>
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white mb-12">
                    {title}
                </h2>
            </Reveal>
            {children}
        </div>
    </section>
);

export default function KdsPage() {
    const { ui, lang } = useContent();
    const t = ui.kds;

    const [sector, setSector] = useState("All");
    const [status, setStatus] = useState("All");
    const [classQuery, setClassQuery] = useState("");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        document.title = t.seoTitle;
        const meta = document.querySelector('meta[name="description"]');
        const prev = meta?.getAttribute("content");
        meta?.setAttribute("content", t.seoDesc);
        window.__lenis ? window.__lenis.scrollTo(0, { immediate: true }) : window.scrollTo(0, 0);
        return () => {
            document.title = "Sadja Core Corp — Building the Future of Energy & Infrastructure";
            if (meta && prev) meta.setAttribute("content", prev);
        };
    }, [t.seoTitle, t.seoDesc]);

    const sectors = ["All", "Industrial", "Infrastructure", "Hospitality"];
    const statuses = ["All", "Completed", "Proposal", "On Progress"];

    const projects = useMemo(
        () =>
            KDS.projects.filter(
                (p) =>
                    (sector === "All" || p.sector === sector) &&
                    (status === "All" || p.status === status)
            ),
        [sector, status]
    );

    const classifications = useMemo(() => {
        const q = classQuery.trim().toLowerCase();
        if (!q) return KDS.classifications;
        return KDS.classifications.filter(
            (c) => c.code.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q)
        );
    }, [classQuery]);

    const scrollToPortfolio = () => {
        const el = document.getElementById("kds-portfolio");
        if (!el) return;
        window.__lenis ? window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 }) : el.scrollIntoView({ behavior: "smooth" });
    };

    const projectName = (p) => p.project || p.client;

    return (
        <main data-testid="kds-page">
            {/* HERO */}
            <section data-testid="kds-hero" className="relative min-h-[88vh] flex flex-col justify-end overflow-hidden">
                <div className="absolute inset-0 kenburns">
                    <img src={KDS.cover} alt="PT. Kharisma Damar Satya — construction company profile cover" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-[#292a24]/70" />
                <div className="absolute inset-0 hero-grid" />
                <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#292a24] to-transparent" />

                <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 lg:px-12 pb-16 pt-40">
                    <Reveal>
                        <Link
                            to="/"
                            data-testid="kds-back-to-sadja-core"
                            className="inline-flex items-center gap-2 font-display text-[11px] font-semibold tracking-[0.2em] uppercase text-[#a2a59d] hover:text-[#f6c422] transition-colors duration-300 mb-10"
                        >
                            <ArrowLeft className="w-4 h-4" /> {t.backTo}
                        </Link>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <div className="w-24 h-24 bg-white rounded-sm p-2.5 flex items-center justify-center mb-8 shadow-xl">
                            <img src={KDS.logo} alt="Damar — We Build For All logo" className="max-w-full max-h-full object-contain" />
                        </div>
                    </Reveal>

                    <p className="overline-text mb-6 flex items-center gap-4">
                        <span className="h-px w-12 bg-[#f6c422]" aria-hidden="true" />
                        {t.category}
                    </p>

                    <h1 className="font-display font-extrabold uppercase leading-[0.98] tracking-tight text-[9vw] sm:text-5xl lg:text-7xl text-white max-w-5xl">
                        <MaskedLine delay={0.25}>{KDS.fullName}</MaskedLine>
                        <MaskedLine delay={0.4} className="text-[#f6c422]">
                            {t.headline1}
                        </MaskedLine>
                        <MaskedLine delay={0.55} className="text-[#f6c422]">
                            {t.headline2}
                        </MaskedLine>
                    </h1>

                    <Reveal delay={0.7}>
                        <p className="text-[#d8d9d2] max-w-2xl mt-6 leading-relaxed text-base lg:text-lg">{t.heroSub}</p>
                        <p className="text-[#a2a59d] max-w-2xl mt-3 leading-relaxed text-sm lg:text-base">{t.heroSupport}</p>
                    </Reveal>

                    <Reveal delay={0.85}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                data-testid="kds-explore-projects"
                                onClick={scrollToPortfolio}
                                className="inline-flex items-center gap-3 bg-[#f6c422] text-[#292a24] font-display font-bold text-xs tracking-[0.18em] uppercase px-8 py-4 hover:bg-[#ffe169] transition-colors duration-300 group"
                            >
                                {t.exploreProjects}
                                <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                            </button>
                            <Link
                                to="/"
                                data-testid="kds-hero-back-home"
                                className="inline-flex items-center gap-3 border border-white/30 text-white font-display font-semibold text-xs tracking-[0.18em] uppercase px-8 py-4 hover:border-[#f6c422] hover:text-[#f6c422] transition-colors duration-300 backdrop-blur-sm"
                            >
                                {t.backTo}
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ABOUT */}
            <Block overline={t.aboutOverline} title={t.aboutTitle} testid="kds-about">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <Reveal>
                        <div className="border border-[#4a4c42] bg-[#24251f] p-8 h-full">
                            <p className="overline-text !text-[10px] mb-6">{t.originalLabel}</p>
                            <div className="space-y-5">
                                {KDS.aboutId.map((p, i) => (
                                    <p key={i} className="text-[#d8d9d2] leading-relaxed text-sm lg:text-base">{p}</p>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="border border-[#f6c422]/40 bg-[#292a24] p-8 h-full">
                            <p className="overline-text !text-[10px] mb-6">{t.translationLabel}</p>
                            <div className="space-y-5">
                                {KDS.aboutEn.map((p, i) => (
                                    <p key={i} className="text-[#d8d9d2] leading-relaxed text-sm lg:text-base">{p}</p>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.15}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                        {[
                            { src: "/assets/kds/inauguration-1.jpg", alt: "Peresmian Proyek Penanggulangan Banjir — PT Summit Adyawinsa Indonesia, KDS team" },
                            { src: "/assets/kds/inauguration-2.jpg", alt: "Flood mitigation project inauguration — full team photo" },
                            { src: "/assets/kds/inauguration-3.jpg", alt: "Flood mitigation project inauguration ceremony — 30 September 2026" },
                            { src: "/assets/kds/land1.jpg", alt: "KDS land development — cut and fill excavation works" },
                            { src: "/assets/kds/land2.jpg", alt: "KDS land development site" },
                        ].map((img) => (
                            <div key={img.src} className="relative overflow-hidden border border-[#4a4c42] group">
                                <img
                                    src={img.src}
                                    alt={img.alt}
                                    className="w-full h-64 object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-[#292a24]/25" />
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-[#a2a59d]/70 mt-4 leading-relaxed">
                        {lang === "id"
                            ? "Dokumentasi: Peresmian Proyek Penanggulangan Banjir — PT Summit Adyawinsa Indonesia (30 September 2026) dan pekerjaan land development (cut & fill)."
                            : "From the field: Flood Mitigation Project inauguration (Peresmian Proyek Penanggulangan Banjir) — PT Summit Adyawinsa Indonesia (30 September 2026), and land development (cut & fill) works."}
                    </p>
                </Reveal>
            </Block>

            {/* VISION & MISSION */}
            <Block overline={t.aboutOverline} title={t.vmTitle} dark testid="kds-vision-mission">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {[
                        { label: t.vision, idText: KDS.visionId, enText: KDS.visionEn, testid: "kds-vision" },
                        { label: t.mission, idText: KDS.missionId, enText: KDS.missionEn, testid: "kds-mission" },
                    ].map((vm, i) => (
                        <Reveal key={vm.label} delay={0.1 * i}>
                            <div data-testid={vm.testid} className="border border-[#4a4c42] bg-[#292a24] p-8 lg:p-10 h-full">
                                <Quote className="w-6 h-6 text-[#f6c422] mb-5" strokeWidth={1.5} />
                                <p className="overline-text !text-[10px] mb-4">{vm.label}</p>
                                <p className="text-white text-lg leading-relaxed">{vm.idText}</p>
                                <div className="mt-6 pt-6 border-t border-[#4a4c42]/60">
                                    <p className="font-display text-[10px] font-semibold tracking-[0.24em] uppercase text-[#a2a59d]/70 mb-3">
                                        {t.translationLabel}
                                    </p>
                                    <p className="text-[#a2a59d] text-sm leading-relaxed italic">{vm.enText}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Block>

            {/* BUSINESS SECTORS */}
            <Block overline={t.sectorsOverline} title={t.sectorsTitle} testid="kds-sectors">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#4a4c42] border border-[#4a4c42]">
                    {KDS.sectors.map((s, i) => {
                        const Icon = ICONS[s.icon];
                        return (
                            <Reveal key={s.num} delay={0.05 * i} className="h-full">
                                <div
                                    data-testid={`kds-sector-card-${s.num}`}
                                    className="group h-full bg-[#292a24] p-7 flex flex-col gap-6 hover:bg-[#32332c] transition-colors duration-500"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="w-12 h-12 border border-[#4a4c42] flex items-center justify-center group-hover:border-[#f6c422] transition-colors duration-500">
                                            <Icon className="w-5 h-5 text-[#f6c422]" strokeWidth={1.5} />
                                        </div>
                                        <span className="font-display text-2xl font-extrabold text-stroke-yellow">{s.num}</span>
                                    </div>
                                    <h3 className="font-display text-lg font-bold uppercase tracking-[0.1em] text-white group-hover:text-[#f6c422] transition-colors duration-300 mt-auto">
                                        {lang === "id" ? s.id : s.en}
                                    </h3>
                                </div>
                            </Reveal>
                        );
                    })}
                    <Reveal delay={0.35} className="h-full">
                        <div className="h-full bg-[#f6c422] p-7 flex flex-col justify-between gap-6">
                            <Building2 className="w-6 h-6 text-[#292a24]" strokeWidth={1.5} />
                            <p className="font-display text-sm font-extrabold uppercase tracking-wide text-[#292a24] leading-snug">
                                {KDS.fullName}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </Block>

            {/* LEGALITY */}
            <Block overline={t.legalityOverline} title={t.legalityTitle} dark testid="kds-legality">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#4a4c42] border border-[#4a4c42]">
                    {KDS.legality.map((l, i) => (
                        <Reveal key={l.label} delay={0.03 * i}>
                            <div data-testid={`kds-legal-${i}`} className="bg-[#24251f] p-6 h-full hover:bg-[#32332c] transition-colors duration-300">
                                <div className="flex items-start gap-3 mb-3">
                                    <FileText className="w-4 h-4 text-[#f6c422] shrink-0 mt-0.5" strokeWidth={1.5} />
                                    <p className="font-display text-[11px] font-bold uppercase tracking-[0.12em] text-white leading-snug">
                                        {l.label}
                                    </p>
                                </div>
                                <p className="text-sm text-[#f6c422] font-medium break-words">{l.value}</p>
                                {l.extra && <p className="text-xs text-[#a2a59d] mt-1.5">{l.extra}</p>}
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Block>

            {/* BUSINESS CLASSIFICATIONS */}
            <Block overline={t.classOverline} title={t.classTitle} testid="kds-classifications">
                <Reveal>
                    <div className="relative max-w-md mb-10">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a2a59d]" strokeWidth={1.5} />
                        <input
                            data-testid="kds-classifications-search"
                            type="text"
                            value={classQuery}
                            onChange={(e) => setClassQuery(e.target.value)}
                            placeholder={t.classSearchPh}
                            aria-label={t.classSearchPh}
                            className="w-full bg-[#32332c] border border-[#4a4c42] text-white placeholder:text-[#a2a59d]/50 pl-11 pr-4 h-12 text-sm focus:outline-none focus:border-[#f6c422] transition-colors duration-300"
                        />
                    </div>
                </Reveal>
                <div className="border-t border-[#4a4c42]">
                    {classifications.map((c) => (
                        <div
                            key={c.code}
                            data-testid={`kds-classification-${c.code}`}
                            className="group grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-8 items-baseline py-5 border-b border-[#4a4c42] hover:bg-[#32332c]/50 transition-colors duration-300 px-2 sm:px-4"
                        >
                            <span className="sm:col-span-2 font-display text-xl font-extrabold tracking-[0.08em] text-[#f6c422]">
                                {c.code}
                            </span>
                            <span className="sm:col-span-10 text-sm text-white/85 leading-relaxed">{c.desc}</span>
                        </div>
                    ))}
                    {classifications.length === 0 && (
                        <p data-testid="kds-classifications-empty" className="py-8 text-sm text-[#a2a59d] px-2 sm:px-4">—</p>
                    )}
                </div>
            </Block>

            {/* CERTIFICATIONS */}
            <Block overline={t.certOverline} title={t.certTitle} dark testid="kds-certifications">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {KDS.certifications.map((c, i) => (
                        <Reveal key={c.name} delay={0.07 * i}>
                            <div
                                data-testid={`kds-cert-${i}`}
                                className="group border border-[#4a4c42] bg-[#292a24] p-8 h-full hover:border-[#f6c422]/60 transition-colors duration-500"
                            >
                                <ShieldCheck className="w-7 h-7 text-[#f6c422] mb-6" strokeWidth={1.5} />
                                <h3 className="font-display text-xl font-extrabold tracking-wide text-white group-hover:text-[#f6c422] transition-colors duration-300">
                                    {c.name}
                                </h3>
                                <p className="font-display text-xs font-semibold tracking-[0.2em] uppercase text-[#a2a59d] mt-3">
                                    {c.id}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </Block>

            {/* PROJECT PORTFOLIO */}
            <section id="kds-portfolio" data-testid="kds-portfolio" className="py-20 lg:py-28">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-4">
                            <span className="h-px w-10 bg-[#f6c422]/60" aria-hidden="true" />
                            <span className="overline-text">{t.portfolioOverline}</span>
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white mb-10">
                            {t.portfolioTitle}
                        </h2>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="space-y-4 mb-12">
                            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t.filterSector}>
                                <span className="overline-text !text-[10px] mr-3 w-16">{t.filterSector}</span>
                                {sectors.map((s) => (
                                    <button
                                        key={s}
                                        data-testid={`kds-filter-sector-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                        onClick={() => setSector(s)}
                                        aria-pressed={sector === s}
                                        className={pill(sector === s)}
                                    >
                                        {s === "All" ? t.filterAll : s}
                                    </button>
                                ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t.filterStatus}>
                                <span className="overline-text !text-[10px] mr-3 w-16">{t.filterStatus}</span>
                                {statuses.map((s) => (
                                    <button
                                        key={s}
                                        data-testid={`kds-filter-status-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                        onClick={() => setStatus(s)}
                                        aria-pressed={status === s}
                                        className={pill(status === s)}
                                    >
                                        {s === "All" ? t.filterAll : s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Reveal>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {projects.map((p, i) => (
                            <Reveal key={p.num} delay={0.04 * (i % 6)} className="h-full">
                                <button
                                    data-testid={`kds-project-card-${p.num}`}
                                    onClick={() => setSelected(p)}
                                    className="group w-full h-full text-left border border-[#4a4c42] bg-[#24251f] overflow-hidden hover:border-[#f6c422]/70 transition-colors duration-500 flex flex-col"
                                >
                                    <div className="relative h-48 overflow-hidden">
                                        <img
                                            src={p.image}
                                            alt={`${projectName(p)} — ${p.location}`}
                                            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                            loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-[#292a24]/35 group-hover:bg-[#292a24]/15 transition-colors duration-500" />
                                        <span className={`absolute top-4 left-4 text-[10px] font-display font-semibold tracking-wider uppercase border px-2.5 py-1 bg-[#292a24]/70 backdrop-blur-sm ${statusColor(p.status)}`}>
                                            {p.status}
                                        </span>
                                        <span className="absolute top-4 right-4 font-display text-[10px] tracking-[0.24em] text-white/70 bg-[#292a24]/70 backdrop-blur-sm px-2.5 py-1">
                                            {p.num}
                                        </span>
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <h3 className="font-display text-base font-bold text-white group-hover:text-[#f6c422] transition-colors duration-300 leading-snug">
                                            {projectName(p)}
                                        </h3>
                                        {p.project && p.client && (
                                            <p className="text-xs text-[#a2a59d] mt-1.5">{p.client}</p>
                                        )}
                                        <div className="mt-4 pt-4 border-t border-[#4a4c42]/60 flex flex-wrap gap-x-5 gap-y-1.5 text-[11px] text-[#a2a59d]">
                                            <span>{p.location}</span>
                                            <span>{p.scope}</span>
                                            <span className="text-[#f6c422]/90">{p.sector}</span>
                                            <span className="ml-auto font-display font-semibold text-white/70">{p.year}</span>
                                        </div>
                                    </div>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                    {projects.length === 0 && (
                        <p data-testid="kds-portfolio-empty" className="text-sm text-[#a2a59d] py-10">—</p>
                    )}
                </div>
            </section>

            {/* PROJECT MODAL */}
            <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
                <DialogContent
                    data-testid="kds-project-modal"
                    className="bg-[#24251f] border border-[#4a4c42] text-white max-w-2xl p-0 overflow-hidden rounded-none"
                >
                    {selected && (
                        <div>
                            <div className="relative h-64 sm:h-72">
                                <img src={selected.image} alt={projectName(selected)} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#24251f] to-transparent" />
                                <span className={`absolute bottom-4 left-6 text-[10px] font-display font-semibold tracking-wider uppercase border px-2.5 py-1 bg-[#292a24]/70 backdrop-blur-sm ${statusColor(selected.status)}`}>
                                    {selected.status}
                                </span>
                            </div>
                            <div className="p-6 sm:p-8">
                                <DialogTitle className="font-display text-2xl font-extrabold text-white leading-tight">
                                    {projectName(selected)}
                                </DialogTitle>
                                <DialogDescription className="sr-only">
                                    {`${projectName(selected)} — ${selected.location}, ${selected.year}`}
                                </DialogDescription>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5 mt-7">
                                    {[
                                        [t.fields.client, selected.client],
                                        [t.fields.location, selected.location],
                                        [t.fields.scope, selected.scope],
                                        [t.fields.sector, selected.sector],
                                        [t.fields.status, selected.status],
                                        [t.fields.year, selected.year],
                                    ]
                                        .filter(([, v]) => v)
                                        .map(([label, value]) => (
                                            <div key={label}>
                                                <p className="overline-text !text-[9px] mb-1.5">{label}</p>
                                                <p className="text-sm text-white/90 leading-snug">{value}</p>
                                            </div>
                                        ))}
                                </div>
                                <button
                                    data-testid="kds-back-to-portfolio"
                                    onClick={() => setSelected(null)}
                                    className="mt-8 inline-flex items-center gap-2 border border-[#f6c422]/60 text-[#f6c422] font-display font-bold text-[11px] tracking-[0.18em] uppercase px-6 py-3 hover:bg-[#f6c422] hover:text-[#292a24] transition-colors duration-300"
                                >
                                    <ArrowLeft className="w-4 h-4" /> {t.backToPortfolio}
                                </button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* OUR EXPERIENCE */}
            <Block overline={t.expOverline} title={t.expTitle} dark testid="kds-experience">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#4a4c42] border border-[#4a4c42]">
                    {KDS.experience.map((e, i) => {
                        const Icon = ICONS[e.icon];
                        const content = lang === "id" ? e.id : e.en;
                        return (
                            <Reveal key={e.en.title} delay={0.05 * i} className="h-full">
                                <div
                                    data-testid={`kds-experience-${i}`}
                                    className="group h-full bg-[#24251f] p-7 hover:bg-[#32332c] transition-colors duration-500 flex flex-col gap-5"
                                >
                                    <Icon className="w-6 h-6 text-[#f6c422]" strokeWidth={1.5} />
                                    <h3 className="font-display text-base font-bold uppercase tracking-[0.1em] text-white group-hover:text-[#f6c422] transition-colors duration-300">
                                        {content.title}
                                    </h3>
                                    <p className="text-sm text-[#a2a59d] leading-relaxed">{content.desc}</p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </Block>

            {/* CONTACT + PDF */}
            <section data-testid="kds-contact" className="py-20 lg:py-28 border-t border-[#4a4c42]">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <Reveal>
                            <div className="flex items-center gap-4 mb-4">
                                <span className="h-px w-10 bg-[#f6c422]/60" aria-hidden="true" />
                                <span className="overline-text">{t.contactOverline}</span>
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-white mb-10">
                                {t.contactTitle}
                            </h2>
                        </Reveal>
                        <div className="space-y-6">
                            <Reveal delay={0.06}>
                                <span data-testid="kds-address" className="flex items-start gap-4 text-sm text-[#a2a59d] leading-relaxed">
                                    <MapPin className="w-5 h-5 text-[#f6c422] shrink-0 mt-0.5" strokeWidth={1.5} />
                                    <span>
                                        <span className="overline-text !text-[9px] block mb-1.5">{t.addressLabel}</span>
                                        {KDS.contact.address.map((line) => (
                                            <span key={line} className="block">{line}</span>
                                        ))}
                                    </span>
                                </span>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <span data-testid="kds-phone" className="flex items-center gap-4 text-sm text-[#d8d9d2]">
                                    <Phone className="w-5 h-5 text-[#f6c422] shrink-0" strokeWidth={1.5} />
                                    <span>
                                        <span className="overline-text !text-[9px] block mb-1">{t.phoneLabel}</span>
                                        {KDS.contact.phone}
                                    </span>
                                </span>
                            </Reveal>
                            <Reveal delay={0.14}>
                                <span data-testid="kds-mobile" className="flex items-center gap-4 text-sm text-[#d8d9d2]">
                                    <Smartphone className="w-5 h-5 text-[#f6c422] shrink-0" strokeWidth={1.5} />
                                    <span>
                                        <span className="overline-text !text-[9px] block mb-1">{t.mobileLabel}</span>
                                        {KDS.contact.mobile}
                                    </span>
                                </span>
                            </Reveal>
                            <Reveal delay={0.18}>
                                <a
                                    data-testid="kds-email-link"
                                    href={`mailto:${KDS.contact.email}`}
                                    className="flex items-center gap-4 text-sm text-[#d8d9d2] hover:text-[#f6c422] transition-colors duration-300"
                                >
                                    <Mail className="w-5 h-5 text-[#f6c422] shrink-0" strokeWidth={1.5} />
                                    <span>
                                        <span className="overline-text !text-[9px] block mb-1">{t.emailLabel}</span>
                                        {KDS.contact.email}
                                    </span>
                                </a>
                            </Reveal>
                            <Reveal delay={0.22}>
                                <a
                                    data-testid="kds-website-link"
                                    href={KDS.contact.websiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-4 text-sm text-[#d8d9d2] hover:text-[#f6c422] transition-colors duration-300"
                                >
                                    <Globe className="w-5 h-5 text-[#f6c422] shrink-0" strokeWidth={1.5} />
                                    <span>
                                        <span className="overline-text !text-[9px] block mb-1">{t.websiteLabel}</span>
                                        {KDS.contact.website}
                                    </span>
                                </a>
                            </Reveal>
                        </div>
                    </div>

                    <Reveal delay={0.12}>
                        <div className="h-full border border-[#f6c422]/40 bg-[#24251f] p-10 flex flex-col justify-center">
                            <p className="overline-text mb-4">{t.profileOverline}</p>
                            <h3 className="font-display text-2xl font-extrabold uppercase text-white leading-tight">
                                {t.downloadTitle}
                            </h3>
                            <p className="text-sm text-[#a2a59d] mt-3 leading-relaxed">{KDS.pdfLabel}</p>
                            <a
                                data-testid="kds-pdf-download-button"
                                href={encodeURI(KDS.pdf)}
                                download
                                className="mt-8 inline-flex items-center justify-center gap-3 bg-[#f6c422] text-[#292a24] font-display font-bold text-xs tracking-[0.18em] uppercase px-8 py-4 hover:bg-[#ffe169] transition-colors duration-300"
                            >
                                <Download className="w-4 h-4" />
                                {t.downloadCta}
                            </a>
                            <Link
                                to="/"
                                data-testid="kds-back-home-link"
                                className="mt-4 inline-flex items-center justify-center gap-2 border border-white/25 text-white font-display font-semibold text-xs tracking-[0.18em] uppercase px-8 py-4 hover:border-[#f6c422] hover:text-[#f6c422] transition-colors duration-300"
                            >
                                {t.backTo} <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
